import type { AppLocale } from '../i18n/i18n';
import type { LocalizedText, MoneyValue } from '../api/types';
import { calendarLocaleTag, normalizeLocalizedNumberInput } from './calendar';

function numericValue(value: string): number | bigint {
  const normalized = normalizeLocalizedNumberInput(value);
  return /^[+-]?\d+$/u.test(normalized) ? BigInt(normalized) : Number(normalized);
}

function displayedFractionDigits(value: string, currency: string): number {
  const normalized = normalizeLocalizedNumberInput(value);
  const supplied = normalized.split('.')[1]?.length ?? 0;
  let currencyDefault = 0;
  if (currency !== 'IRT') {
    try {
      currencyDefault = new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions().minimumFractionDigits ?? 0;
    } catch {
      // Private accounting and asset units are intentionally not all ISO 4217 codes.
    }
  }
  return Math.min(20, Math.max(currencyDefault, supplied));
}

/** Legacy API payloads remain bilingual; Arabic uses its own value when available and otherwise English. */
export function localize(text: LocalizedText, locale: AppLocale): string {
  if (locale === 'fa') return text.fa;
  if (locale === 'ar') return text.ar ?? text.en;
  return text.en;
}

/** Codes are always retained so Rial and Toman can never be inferred from magnitude. */
export function currencyLabel(code: string, locale?: AppLocale): string {
  const normalized = code.trim().toUpperCase();
  if (normalized === 'IRR') {
    if (locale === 'ar') return 'IRR (ريال إيراني)';
    if (locale === 'en') return 'IRR (Iranian rial)';
    return 'IRR (ریال)';
  }
  if (normalized === 'IRT') {
    if (locale === 'ar') return 'IRT (تومان إيراني)';
    if (locale === 'en') return 'IRT (Iranian toman)';
    return 'IRT (تومان)';
  }
  if (normalized === 'USD') return '$ (USD)';
  return normalized;
}

export function formatMoney(value: MoneyValue, locale: AppLocale, compact = false): string {
  const fractionDigits = displayedFractionDigits(value.amount, value.currency.toUpperCase());
  const formatted = new Intl.NumberFormat(calendarLocaleTag(locale), {
    notation: compact ? 'compact' : 'standard',
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(numericValue(value.amount));
  return `${formatted} ${currencyLabel(value.currency, locale)}`;
}

export function formatNumber(value: number | string, locale: AppLocale): string {
  const parsed = typeof value === 'number' ? value : numericValue(value);
  return new Intl.NumberFormat(calendarLocaleTag(locale), { maximumFractionDigits: 4 }).format(parsed);
}

const relativeUnits: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 365 * 24 * 60 * 60_000],
  ['month', 30 * 24 * 60 * 60_000],
  ['day', 24 * 60 * 60_000],
  ['hour', 60 * 60_000],
  ['minute', 60_000],
  ['second', 1_000],
];

export function formatRelativeTime(value: string, locale: AppLocale, now = Date.now()): string {
  const elapsed = Date.parse(value) - now;
  if (Number.isNaN(elapsed)) return '';

  const format = new Intl.RelativeTimeFormat(calendarLocaleTag(locale), { numeric: 'auto' });
  const magnitude = Math.abs(elapsed);
  const [unit, span] = relativeUnits.find(([, size]) => magnitude >= size) ?? ['second', 1_000] as const;
  return format.format(Math.round(elapsed / span), unit);
}

export function formatDate(value: string, locale: AppLocale, includeTime = false): string {
  const date = new Date(value.includes('T') ? value : `${value}T00:00:00Z`);
  return new Intl.DateTimeFormat(calendarLocaleTag(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
}
